import { defineStore } from "pinia";
import { computed, ref } from "vue";

import {
  getConnectedServicesSummary,
  getMailboxOAuthStatus,
} from "@/api/connectedServices";
import { useUserStore } from "@/stores/user";
import type {
  ConnectedServicesMailbox,
  ConnectedServicesPhase,
  ConnectedServicesPlatform,
  ConnectedServicesRuntime,
  ConnectedServicesSnapshot,
  ConnectedServicesSummaryResponse,
} from "@/types/connectedServices";

const SIGN_IN_REQUIRED_MESSAGE = "Sign in is required to view connected services.";
const SUMMARY_FALLBACK_MESSAGE = "Connected services could not be loaded. Please try again.";

function errorMessage(caught: unknown): string {
  return caught instanceof Error && caught.message ? caught.message : SUMMARY_FALLBACK_MESSAGE;
}

function isAbortError(caught: unknown): boolean {
  return caught instanceof DOMException && caught.name === "AbortError"
    || typeof caught === "object"
      && caught !== null
      && "name" in caught
      && (caught as { name?: unknown }).name === "AbortError";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

function isRuntime(value: unknown): value is ConnectedServicesRuntime {
  if (!isRecord(value)) return false;
  const capabilities = value.capabilities;
  return (
    (value.state === "active" || value.state === "idle" || value.state === "never_used" || value.state === "unavailable")
    && typeof value.active_sessions === "number"
    && Number.isInteger(value.active_sessions)
    && value.active_sessions >= 0
    && (typeof value.last_activity_at === "string" || value.last_activity_at === null)
    && (typeof value.region === "string" || value.region === null)
    && typeof value.encrypted_storage === "string"
    && isRecord(capabilities)
    && isBoolean(capabilities.view)
    && isBoolean(capabilities.reset)
  );
}

function isPlatform(value: unknown): value is ConnectedServicesPlatform {
  if (!isRecord(value) || typeof value.id !== "string" || typeof value.name !== "string") return false;
  const capabilities = value.capabilities;
  return (
    value.source_recognition === "supported"
    && value.account_connection === "unavailable"
    && isRecord(capabilities)
    && isBoolean(capabilities.connect)
    && isBoolean(capabilities.manage)
  );
}

function isSummary(value: unknown): value is ConnectedServicesSummaryResponse {
  return isRecord(value)
    && isRuntime(value.runtime)
    && Array.isArray(value.platforms)
    && value.platforms.every(isPlatform);
}

function mailboxState(connected: boolean): ConnectedServicesMailbox["state"] {
  return connected ? "connected" : "not_connected";
}

function isMailboxStatus(value: unknown): value is { oauth: { connected: boolean } } {
  return isRecord(value)
    && isRecord(value.oauth)
    && isBoolean(value.oauth.connected);
}

/**
 * The backend normally returns platform rows. Empty is reserved for the
 * synthetic no-service response: no platforms, never-used runtime, no active
 * sessions or activity, and no connected mailbox.
 */
export function isConnectedServicesEmpty(snapshot: ConnectedServicesSnapshot): boolean {
  return (
    snapshot.platforms.length === 0
    && snapshot.runtime.state === "never_used"
    && snapshot.runtime.active_sessions === 0
    && snapshot.runtime.last_activity_at === null
    && snapshot.mailbox.state !== "connected"
  );
}

export const useConnectedServicesStore = defineStore("connectedServices", () => {
  const userStore = useUserStore();
  const phase = ref<ConnectedServicesPhase>("idle");
  const snapshot = ref<ConnectedServicesSnapshot | null>(null);
  const error = ref("");
  let activeController: AbortController | null = null;
  let generation = 0;

  const busy = computed(() => phase.value === "loading" || phase.value === "updating");
  const hasData = computed(() => snapshot.value !== null);

  function owns(controller: AbortController, requestGeneration: number): boolean {
    return activeController === controller
      && generation === requestGeneration
      && !controller.signal.aborted;
  }

  function beginRequest(): { controller: AbortController; requestGeneration: number } {
    activeController?.abort();
    const controller = new AbortController();
    activeController = controller;
    return { controller, requestGeneration: ++generation };
  }

  async function load(): Promise<void> {
    const hasPreviousSnapshot = snapshot.value !== null;
    const { controller, requestGeneration } = beginRequest();
    phase.value = hasPreviousSnapshot ? "updating" : "loading";
    error.value = "";
    try {
      const user = await userStore.ensureSession();
      if (!owns(controller, requestGeneration)) return;
      if (!user) throw new Error(SIGN_IN_REQUIRED_MESSAGE);

      const [summaryResult, mailboxResult] = await Promise.allSettled([
        getConnectedServicesSummary(controller.signal),
        getMailboxOAuthStatus(controller.signal),
      ]);
      if (!owns(controller, requestGeneration)) return;

      if (summaryResult.status === "rejected") {
        if (isAbortError(summaryResult.reason)) return;
        const detail = errorMessage(summaryResult.reason);
        error.value = hasPreviousSnapshot
          ? `The latest connected services refresh failed; showing stale data. ${detail}`
          : detail;
        phase.value = "error";
        return;
      }
      if (!isSummary(summaryResult.value)) {
        const detail = SUMMARY_FALLBACK_MESSAGE;
        error.value = hasPreviousSnapshot
          ? `The latest connected services refresh failed; showing stale data. ${detail}`
          : detail;
        phase.value = "error";
        return;
      }
      if (mailboxResult.status === "rejected" && isAbortError(mailboxResult.reason)) return;

      const mailbox: ConnectedServicesMailbox = {
        account_email: user.email.trim(),
        forwarding: "not_reported",
        last_sync_at: null,
        state: mailboxResult.status === "fulfilled" && isMailboxStatus(mailboxResult.value)
          ? mailboxState(mailboxResult.value.oauth.connected)
          : "unavailable",
        verification: "not_reported",
      };
      const nextSnapshot: ConnectedServicesSnapshot = {
        platforms: summaryResult.value.platforms,
        runtime: summaryResult.value.runtime,
        mailbox,
      };
      if (!owns(controller, requestGeneration)) return;
      snapshot.value = nextSnapshot;
      phase.value = isConnectedServicesEmpty(nextSnapshot) ? "empty" : "success";
    } catch (caught: unknown) {
      if (!owns(controller, requestGeneration) || isAbortError(caught)) return;
      const detail = caught instanceof Error && caught.message
        ? caught.message
        : SIGN_IN_REQUIRED_MESSAGE;
      error.value = hasPreviousSnapshot
        ? `The latest connected services refresh failed; showing stale data. ${detail}`
        : detail;
      phase.value = "error";
    } finally {
      if (activeController === controller) activeController = null;
    }
  }

  function refresh(): Promise<void> {
    return load();
  }

  function dispose(): void {
    generation += 1;
    activeController?.abort();
    activeController = null;
    if (phase.value === "updating") {
      phase.value = snapshot.value && isConnectedServicesEmpty(snapshot.value) ? "empty" : snapshot.value ? "success" : "idle";
    } else if (phase.value === "loading" && !snapshot.value) {
      phase.value = "idle";
    }
  }

  function reset(): void {
    generation += 1;
    activeController?.abort();
    activeController = null;
    phase.value = "idle";
    snapshot.value = null;
    error.value = "";
  }

  return {
    busy,
    dispose,
    error,
    hasData,
    load,
    phase,
    refresh,
    reset,
    snapshot,
  };
});
